import type { HoverIdleNavIconName } from "@/app/game/navigationIcons";
import { getMessages } from "@/i18n/runtime";

export interface NavItemDef {
  readonly id: string;
  readonly label: string;
  readonly screenLabel: string;
  readonly draft: boolean;
  readonly click: boolean;
  readonly icon?: HoverIdleNavIconName;
}

export interface GameNavigationAvailability {
  readonly mpCapacity: number;
}

export const DEFAULT_GAMEPLAY_SCREEN_ID = "hovTarget";

const BASE_NAV_ITEMS: readonly NavItemDef[] = [
  {
    id: "hovTarget",
    label: "Hov",
    screenLabel: "Hov biome",
    draft: false,
    click: false,
  },
  {
    id: "timTarget",
    label: "Tim",
    screenLabel: "Tim biome",
    draft: true,
    click: false,
  },
  {
    id: "coreloop3",
    label: "Mat",
    screenLabel: "Mat biome",
    draft: true,
    click: false,
  },
  {
    id: "coreloop4",
    label: "Lev",
    screenLabel: "Lev biome",
    draft: true,
    click: false,
  },
  {
    id: "mpm",
    label: "MPM",
    screenLabel: "MPM",
    draft: false,
    click: false,
    icon: "mpm",
  },
  {
    id: "achievements",
    label: "実績",
    screenLabel: "実績",
    draft: false,
    click: false,
    icon: "achievements",
  },
  {
    id: "records",
    label: "記録",
    screenLabel: "記録",
    draft: false,
    click: false,
    icon: "records",
  },
  {
    id: "help",
    label: "Help",
    screenLabel: "Help",
    draft: false,
    click: false,
    icon: "help",
  },
  {
    id: "settings",
    label: "設定",
    screenLabel: "設定",
    draft: false,
    click: true,
    icon: "settings",
  },
  {
    id: "payment",
    label: "Shop",
    screenLabel: "Shop",
    draft: false,
    click: true,
    icon: "shop",
  },
];

// Development entries keep their own labels instead of a shared-catalog lookup
// so a production build ships neither their navigation nor screen copy.
const BALANCE_LAB_DEV_NAV_ITEM: NavItemDef = {
  id: "balanceLab",
  label: "Balance",
  screenLabel: "Balance Lab",
  draft: false,
  click: false,
};

const ADMIN_DEV_NAV_ITEM: NavItemDef = {
  id: "admin",
  label: "管理",
  screenLabel: "管理者",
  draft: false,
  click: false,
};

const BASE_CUSTOM_CONTENT_SCREEN_IDS = [
  "hovTarget",
  "timTarget",
  "mpm",
  "settings",
  "achievements",
  "records",
  "help",
  "payment",
] as const;

export function createGameNavItems(options: {
  readonly includeAdmin: boolean;
  readonly includeBalanceLab?: boolean;
}): readonly NavItemDef[] {
  const messages = getMessages();
  const localizedBaseItems = BASE_NAV_ITEMS.map((item) => ({
    ...item,
    label:
      messages.navigation.items[
        item.id as keyof typeof messages.navigation.items
      ].label,
    screenLabel:
      messages.navigation.items[
        item.id as keyof typeof messages.navigation.items
      ].screenLabel,
  }));

  const developmentItems: NavItemDef[] = [];
  if (__HOVER_IDLE_BALANCE_LAB_BUILD__ && options.includeBalanceLab === true) {
    developmentItems.push(BALANCE_LAB_DEV_NAV_ITEM);
  }
  if (__HOVER_IDLE_ADMIN_PANEL_BUILD__ && options.includeAdmin) {
    developmentItems.push(ADMIN_DEV_NAV_ITEM);
  }
  return [...localizedBaseItems, ...developmentItems];
}

export function createCustomContentScreenIds(options: {
  readonly includeAdmin: boolean;
  readonly includeBalanceLab?: boolean;
}): ReadonlySet<string> {
  const ids = new Set<string>(BASE_CUSTOM_CONTENT_SCREEN_IDS);
  if (__HOVER_IDLE_BALANCE_LAB_BUILD__ && options.includeBalanceLab === true) {
    ids.add("balanceLab");
  }
  if (__HOVER_IDLE_ADMIN_PANEL_BUILD__ && options.includeAdmin) {
    ids.add("admin");
  }
  return ids;
}

export function isGameNavItemVisible(
  item: Pick<NavItemDef, "id" | "draft">,
  availability: GameNavigationAvailability,
): boolean {
  if (item.draft) {
    return false;
  }
  if (item.id === "mpm") {
    return availability.mpCapacity > 0;
  }
  return true;
}

export function resolveAvailableGameScreenId(
  id: string,
  availability: GameNavigationAvailability,
): string {
  if (id === "mpm" && availability.mpCapacity <= 0) {
    return DEFAULT_GAMEPLAY_SCREEN_ID;
  }
  return id;
}
